import { AxisAlignedBoundingBox, Cartesian3 } from "cesium";
export const TOTAL_CELL_COUNT_FOR_MEMORY_OPTIMIZATION = 10000;
export const MIN_CELL_SIZE = 0.1;
export const NEAR_ZERO_THRESHOLD = 1e-8;
export var HeightMapUtils;
(function (HeightMapUtils) {
    // This function also checks if the indexes are outside of the bounding box
    function getArrayIndex(xIndex, yIndex, heightMapInfo) {
        let index = undefined;
        // Indexes should be inclusive of the min and max values
        if (heightMapInfo.minIndexes[0] < xIndex &&
            heightMapInfo.maxIndexes[0] > xIndex &&
            heightMapInfo.minIndexes[1] < yIndex &&
            heightMapInfo.maxIndexes[1] > yIndex) {
            index =
                heightMapInfo.boxesInY * (xIndex - heightMapInfo.minIndexes[0]) +
                    (yIndex - heightMapInfo.minIndexes[1]);
        }
        return index;
    }
    HeightMapUtils.getArrayIndex = getArrayIndex;
    function getHeightByArrayIndex(xIndex, yIndex, heightMapInfo) {
        const heightIndex = getArrayIndex(xIndex, yIndex, heightMapInfo);
        const pointCloudHeight = heightMapInfo.heights[heightIndex];
        return pointCloudHeight;
    }
    HeightMapUtils.getHeightByArrayIndex = getHeightByArrayIndex;
    function getIndexForPoint(point, voxelSize) {
        return {
            xIndex: getIndexForCoordinate(point.x, voxelSize),
            yIndex: getIndexForCoordinate(point.y, voxelSize),
        };
    }
    HeightMapUtils.getIndexForPoint = getIndexForPoint;
    function getBoxByIndex(xIndex, yIndex, heightMapInfo) {
        const center = getCenter(xIndex, yIndex, true, heightMapInfo);
        const pMinTop = new Cartesian3(center.x - 0.5 * heightMapInfo.boxSize, center.y - 0.5 * heightMapInfo.boxSize, center.z);
        const pMaxTop = new Cartesian3(center.x + 0.5 * heightMapInfo.boxSize, center.y + 0.5 * heightMapInfo.boxSize, center.z);
        const zSize = Math.abs(heightMapInfo.boxMax[2] - heightMapInfo.boxMin[2]);
        const pMinBottom = new Cartesian3(center.x - 0.5 * heightMapInfo.boxSize, center.y - 0.5 * heightMapInfo.boxSize, center.z - zSize);
        const pMaxBottom = new Cartesian3(center.x + 0.5 * heightMapInfo.boxSize, center.y + 0.5 * heightMapInfo.boxSize, center.z - zSize);
        const box = AxisAlignedBoundingBox.fromPoints([
            pMinTop,
            pMaxTop,
            pMinBottom,
            pMaxBottom,
        ]);
        return box;
    }
    HeightMapUtils.getBoxByIndex = getBoxByIndex;
    function getIndexForCoordinate(coord, boxSize) {
        return Math.floor(coord / boxSize);
    }
    HeightMapUtils.getIndexForCoordinate = getIndexForCoordinate;
    function getHeight(point, heighatMapInfo) {
        const indexes = HeightMapUtils.getIndexForPoint(point, heighatMapInfo.boxSize);
        const height = HeightMapUtils.getHeightByArrayIndex(indexes.xIndex, indexes.yIndex, heighatMapInfo);
        return height;
    }
    HeightMapUtils.getHeight = getHeight;
    function getCenter(xIndex, yIndex, includeHeight, heighatMapInfo) {
        const voxelSize = heighatMapInfo.boxSize;
        const height = includeHeight
            ? HeightMapUtils.getHeightByArrayIndex(xIndex, yIndex, heighatMapInfo)
            : 0;
        const voxelCenter = new Cartesian3(xIndex * voxelSize + voxelSize / 2, yIndex * voxelSize + voxelSize / 2, height);
        return voxelCenter;
    }
    HeightMapUtils.getCenter = getCenter;
    function getBarycentricPointWeights(p1, p2, p3, point) {
        const determinant = (p2.y - p3.y) * (p1.x - p3.x) + (p3.x - p2.x) * (p1.y - p3.y);
        const w1 = ((p2.y - p3.y) * (point.x - p3.x) + (p3.x - p2.x) * (point.y - p3.y)) /
            determinant;
        const w2 = ((p3.y - p1.y) * (point.x - p3.x) + (p1.x - p3.x) * (point.y - p3.y)) /
            determinant;
        const w3 = 1 - w1 - w2;
        return [w1, w2, w3];
    }
    HeightMapUtils.getBarycentricPointWeights = getBarycentricPointWeights;
    /**
     * Calculates Average cell height from two heightmap of different cell sizes.
     * @param sourceInfo {xIndex, yIndex, cellsize} - cellsize is typically a multiple of 0.1
     * @param heightMapInfo target entity height map information - boxSize is typically 0.1 (fixed)
     * @returns Average Height of cell
     */
    function getAvgHeightForCellSize(sourceInfo, heightMapInfo) {
        // Multiply by 10 to use integer arithmetic and avoid floating-point precision issues
        // (e.g., 0.7 / 0.1 = 6.999... in direct division, but (0.7 * 10) / (0.1 * 10) = 7)
        const ratio = (sourceInfo.cellsize * 10) / (heightMapInfo.boxSize * 10);
        const roundedRatio = Math.round(ratio);
        const pointHeightInCell = [];
        if (sourceInfo.cellsize > heightMapInfo.boxSize &&
            Math.abs(roundedRatio - ratio) < Number.EPSILON) {
            const newIndex = { xIndex: sourceInfo.xIndex, yIndex: sourceInfo.yIndex };
            const minIndex = {
                xIndex: newIndex.xIndex,
                yIndex: newIndex.yIndex,
            };
            const maxIndex = {
                xIndex: newIndex.xIndex + 1,
                yIndex: newIndex.yIndex + 1,
            };
            const startIndex = {
                xIndex: Math.round(minIndex.xIndex * ratio),
                yIndex: Math.round(minIndex.yIndex * ratio),
            };
            const endIndex = {
                xIndex: Math.round(maxIndex.xIndex * ratio),
                yIndex: Math.round(maxIndex.yIndex * ratio),
            };
            for (let i = startIndex.xIndex; i <= endIndex.xIndex; ++i) {
                for (let j = startIndex.yIndex; j <= endIndex.yIndex; ++j) {
                    const height = HeightMapUtils.getHeightByArrayIndex(i, j, heightMapInfo);
                    if (height !== undefined && !isNaN(height)) {
                        pointHeightInCell.push(height);
                    }
                }
            }
        }
        else {
            throw new Error(`Invalid cell size configuration: sourceInfo.cellsize=${sourceInfo.cellsize}, heightMapInfo.boxSize=${heightMapInfo.boxSize}`);
        }
        if (pointHeightInCell.length > 0) {
            return (pointHeightInCell.reduce((a, b) => a + b, 0) / pointHeightInCell.length);
        }
        return undefined;
    }
    HeightMapUtils.getAvgHeightForCellSize = getAvgHeightForCellSize;
    function getCellSizeForTotalCellCount(totalCellCount = TOTAL_CELL_COUNT_FOR_MEMORY_OPTIMIZATION, boundingBox) {
        // assumption
        // Minimum cell size is 0.1
        // Any cell size should be multiple of 0.1
        const length = boundingBox.maximum.x - boundingBox.minimum.x;
        const width = boundingBox.maximum.y - boundingBox.minimum.y;
        const cellSize = Math.sqrt((length * width) / totalCellCount);
        const roundedCellSize = Math.round(cellSize * 10) / 10;
        return roundedCellSize < MIN_CELL_SIZE ? MIN_CELL_SIZE : roundedCellSize;
    }
    HeightMapUtils.getCellSizeForTotalCellCount = getCellSizeForTotalCellCount;
})(HeightMapUtils || (HeightMapUtils = {}));
//# sourceMappingURL=utils.js.map